import { assertUnreachable } from 'lib/staticAssert/staticAssertUtils'
import type {
  AssistantSurfaceContextFor,
  AssistantSurfaceContextSnapshot,
  AssistantSurfaceId,
} from 'tripPlanner/assistant/api/types'

export interface AssistantTurnPolicySnapshot {
  currentSurface: AssistantSurfaceId
  regenerationScopeKey?: string
}

type AccommodationOfferPageIdentity = AssistantSurfaceContextFor<'accommodation_offer_page'>['pageIdentity']

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function isAssistantSurfaceId(value: unknown): value is AssistantSurfaceId {
  switch (value) {
    case 'trip_planner':
    case 'accommodation_offer_page':
      return true
    default:
      return false
  }
}

export function buildAccommodationOfferPageRegenerationScopeKey(
  identity: AccommodationOfferPageIdentity,
): string | undefined {
  const { offerId, propertyId } = identity

  if (!offerId) {
    return propertyId ? `accommodation_offer_page:property:${propertyId}` : undefined
  }

  return propertyId ?
    `accommodation_offer_page:offer:${offerId}:property:${propertyId}` :
    `accommodation_offer_page:offer:${offerId}`
}

export function buildAssistantTurnPolicySnapshot(
  surfaceContextSnapshot: AssistantSurfaceContextSnapshot,
): AssistantTurnPolicySnapshot {
  switch (surfaceContextSnapshot.surface) {
    case 'trip_planner':
      return {
        currentSurface: 'trip_planner',
        regenerationScopeKey: 'trip_planner',
      }
    case 'accommodation_offer_page': {
      const regenerationScopeKey = buildAccommodationOfferPageRegenerationScopeKey(
        surfaceContextSnapshot.surfaceContext.pageIdentity,
      )

      return {
        currentSurface: 'accommodation_offer_page',
        ...(regenerationScopeKey ? { regenerationScopeKey } : {}),
      }
    }
    default:
      return assertUnreachable(surfaceContextSnapshot)
  }
}

export function isAssistantTurnPolicySnapshot(value: unknown): value is AssistantTurnPolicySnapshot {
  if (!isRecord(value)) {
    return false
  }

  const { currentSurface, regenerationScopeKey } = value
  return isAssistantSurfaceId(currentSurface) &&
    (regenerationScopeKey === undefined || typeof regenerationScopeKey === 'string')
}

export function isTripPlannerRegenerationCompatible(snapshot: unknown): boolean {
  if (snapshot === undefined) {
    return true
  }

  return isAssistantTurnPolicySnapshot(snapshot) && snapshot.currentSurface === 'trip_planner'
}

export function isOfferPageRegenerationCompatible(
  snapshot: unknown,
  currentRegenerationScopeKey: string | undefined,
): boolean {
  if (!currentRegenerationScopeKey || !isAssistantTurnPolicySnapshot(snapshot)) {
    return false
  }

  return snapshot.currentSurface === 'accommodation_offer_page' &&
    snapshot.regenerationScopeKey === currentRegenerationScopeKey
}
